<p-toast></p-toast>
<p-progressBar mode="indeterminate" class="m-2" *ngIf="isLoading"></p-progressBar>

<app-page-shell>
    <app-page-hero eyebrow="Reports" title="Profit & Loss" subtitle="Inspect revenue, expenses, and net performance by period.">
        <div actions class="report-actions">
            <p-button label="Generate" icon="pi pi-refresh" (onClick)="loadProfitAndLossData({})"></p-button>
            <p-button label="Export" icon="pi pi-file-excel" severity="success" (onClick)="exportExcel()"></p-button>
            <p-button label="Download PDF" icon="pi pi-file-pdf" severity="secondary" [outlined]="true" [loading]="reportActions.isPending('download')" (onClick)="printReport(true)"></p-button>
            <p-button label="Print" icon="pi pi-print" severity="secondary" [outlined]="true" [loading]="reportActions.isPending('print')" (onClick)="printReport()"></p-button>
            <app-report-email reportName="Profit and Loss" [rows]="incomeItems.concat(expenseItems)"></app-report-email>
        </div>
    </app-page-hero>

    <app-page-form-section icon="pi pi-filter" title="Report Filters" subtitle="Choose the date range and comparison period.">
        <div class="report-filter-grid">
            <p-iftalabel>
                <p-datePicker inputId="startDate" [(ngModel)]="startDate" dateFormat="yy-mm-dd" [showIcon]="true" styleClass="w-full" (onSelect)="loadProfitAndLossData({})"></p-datePicker>
                <label for="startDate">Start Date</label>
            </p-iftalabel>
            <p-iftalabel>
                <p-datePicker inputId="endDate" [(ngModel)]="endDate" dateFormat="yy-mm-dd" [showIcon]="true" styleClass="w-full" (onSelect)="loadProfitAndLossData({})"></p-datePicker>
                <label for="endDate">End Date</label>
            </p-iftalabel>
            <p-iftalabel>
                <p-select inputId="periodType" [options]="periodOptions" [(ngModel)]="selectedPeriodType" optionLabel="name" optionValue="value"
                          (onChange)="onPeriodTypeChange()" placeholder="Select period type" styleClass="w-full"></p-select>
                <label for="periodType">Period Type</label>
            </p-iftalabel>
            <p-iftalabel>
                <app-logistics-project-lookup inputId="logisticsProjectId" [(ngModel)]="selectedLogisticsProjectId" (ngModelChange)="loadProfitAndLossData({})"></app-logistics-project-lookup>
                <label for="logisticsProjectId">Logistics Project</label>
            </p-iftalabel>
        </div>
    </app-page-form-section>

    <app-page-data-panel *ngIf="diagnostics.length" title="Report Diagnostics" [resultText]="diagnostics.length + ' checks'">
        <p-table [value]="diagnostics" styleClass="p-datatable-gridlines" [tableStyle]="{ 'min-width': '60rem' }">
            <ng-template pTemplate="header">
                <tr>
                    <th>Severity</th>
                    <th>Code</th>
                    <th>Message</th>
                    <th>Reference</th>
                    <th class="text-right">Amount</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-diagnostic>
                <tr>
                    <td>{{ diagnostic.severity }}</td>
                    <td>{{ diagnostic.code }}</td>
                    <td>{{ diagnostic.message }}</td>
                    <td>{{ diagnostic.referenceNumber || diagnostic.referenceId || '-' }}</td>
                    <td class="text-right">{{ diagnostic.amount ?? '-' }}</td>
                </tr>
            </ng-template>
        </p-table>
    </app-page-data-panel>

    <div class="grid">
        <div class="col-12 md:col-4">
            <p-card header="Total Revenue" styleClass="text-center report-metric-card">
                <div class="text-2xl font-bold" [ngClass]="{'text-green-600': totalRevenue > 0, 'text-red-600': totalRevenue < 0}">
                    {{ totalRevenue | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}
                </div>
            </p-card>
        </div>
        <div class="col-12 md:col-4">
            <p-card header="Total Expenses" styleClass="text-center report-metric-card">
                <div class="text-2xl font-bold" [ngClass]="{'text-red-600': totalExpenses > 0, 'text-green-600': totalExpenses < 0}">
                    {{ totalExpenses | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}
                </div>
            </p-card>
        </div>
        <div class="col-12 md:col-4">
            <p-card header="Net Profit/Loss" styleClass="text-center report-metric-card">
                <div class="text-2xl font-bold" [ngClass]="{'text-green-600': netProfitLoss > 0, 'text-red-600': netProfitLoss < 0}">
                    {{ netProfitLoss | currency: reportCurrencyCode : 'symbol' : '1.2-2' }}
                </div>
            </p-card>
        </div>
    </div>

<div class="profit-loss-sections" [class.yearly-side-by-side]="selectedPeriodType === 'yearly'">
<!-- Income Table -->
<app-page-data-panel title="Income" class="profit-loss-section income-section">
    <p-table [value]="incomeItems" dataKey="accountId" [resizableColumns]="true" styleClass="accounting-tree-table"
             [rows]="10" [paginator]="true" [totalRecords]="totalIncomeRecords" [showCurrentPageReport]="true"
             currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries">
        <ng-template pTemplate="header">
            <tr>
                <th pSortableColumn="accountCode" class="report-account-column">Account <p-sortIcon field="accountCode"></p-sortIcon></th>
                <th *ngFor="let period of selectedPeriods" class="text-right" [pSortableColumn]="'netChanges.' + period">
                    {{ period }} <p-sortIcon [field]="'netChanges.' + period"></p-sortIcon>
                </th>
            </tr>
        </ng-template>
        <ng-template pTemplate="body" let-item>
            <tr [ngClass]="{'report-summary-row': item.isSummaryRow, 'report-root-row': item.level === 0, 'report-direct-row': item.isDirectPostingRow}">
                <td>
                    <app-accounting-report-tree-cell
                        [accountCode]="item.accountCode"
                        [accountName]="item.accountName"
                        [level]="item.level"
                        [childCount]="item.children?.length || 0"
                        [expanded]="!!item.expanded"
                        [summary]="!!item.isSummaryRow"
                        [direct]="!!item.isDirectPostingRow"
                        directLabel="Direct posting"
                        (toggled)="toggleNode(item)">
                    </app-accounting-report-tree-cell>
                </td>
                <td *ngFor="let period of selectedPeriods" class="report-value-cell"
                    [ngClass]="{'report-value-credit': getBalanceSide(item.netChanges[period], item.plCategory) === 'CR', 'report-value-zero': getBalanceSide(item.netChanges[period], item.plCategory) === 'ZERO'}">
                    <div class="report-value-stack">
                        <span class="report-value">{{ item.netChanges[period] | currency:reportCurrencyCode:'symbol':'1.2-2' }}</span>
                        <span class="report-balance-side">{{ getBalanceSide(item.netChanges[period], item.plCategory) }}</span>
                    </div>
                </td>
            </tr>
        </ng-template>
        <ng-template pTemplate="footer">
            <tr class="bg-gray-200 font-bold text-lg">
                <td class="report-total-label">Total Income</td>
                <td *ngFor="let period of selectedPeriods" class="text-right">
                    {{ getTotalForCategory('Income', period) | currency:reportCurrencyCode:'symbol':'1.2-2' }}
                </td>
            </tr>
        </ng-template>
    </p-table>
</app-page-data-panel>

<!-- Expenses Table -->
<app-page-data-panel title="Expenses" class="profit-loss-section expense-section">
    <p-table [value]="expenseItems" dataKey="accountId" [resizableColumns]="true" styleClass="accounting-tree-table"
             [rows]="10" [paginator]="true" [totalRecords]="totalExpenseRecords" [showCurrentPageReport]="true"
             currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries">
        <ng-template pTemplate="header">
            <tr>
                <th pSortableColumn="accountCode" class="report-account-column">Account <p-sortIcon field="accountCode"></p-sortIcon></th>
                <th *ngFor="let period of selectedPeriods" class="text-right" [pSortableColumn]="'netChanges.' + period">
                    {{ period }} <p-sortIcon [field]="'netChanges.' + period"></p-sortIcon>
                </th>
            </tr>
        </ng-template>
        <ng-template pTemplate="body" let-item>
            <tr [ngClass]="{'report-summary-row': item.isSummaryRow, 'report-root-row': item.level === 0, 'report-direct-row': item.isDirectPostingRow}">
                <td>
                    <app-accounting-report-tree-cell
                        [accountCode]="item.accountCode"
                        [accountName]="item.accountName"
                        [level]="item.level"
                        [childCount]="item.children?.length || 0"
                        [expanded]="!!item.expanded"
                        [summary]="!!item.isSummaryRow"
                        [direct]="!!item.isDirectPostingRow"
                        directLabel="Direct posting"
                        (toggled)="toggleNode(item)">
                    </app-accounting-report-tree-cell>
                </td>
                <td *ngFor="let period of selectedPeriods" class="report-value-cell"
                    [ngClass]="{'report-value-credit': getBalanceSide(item.netChanges[period], item.plCategory) === 'CR', 'report-value-zero': getBalanceSide(item.netChanges[period], item.plCategory) === 'ZERO'}">
                    <div class="report-value-stack">
                        <span class="report-value">{{ item.netChanges[period] | currency:reportCurrencyCode:'symbol':'1.2-2' }}</span>
                        <span class="report-balance-side">{{ getBalanceSide(item.netChanges[period], item.plCategory) }}</span>
                    </div>
                </td>
            </tr>
        </ng-template>
        <ng-template pTemplate="footer">
            <tr class="bg-gray-200 font-bold text-lg">
                <td class="report-total-label">Total Expenses</td>
                <td *ngFor="let period of selectedPeriods" class="text-right">
                    {{ getTotalForCategory('Expenses', period) | currency:reportCurrencyCode:'symbol':'1.2-2' }}
                </td>
            </tr>
        </ng-template>
    </p-table>
</app-page-data-panel>
</div>
</app-page-shell>
